import { MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { ConsentContentLocale } from "../hooks/useConsentGovernanceLocales";

type ConsentGovernanceHeaderProps = {
    availableContentLocales: ConsentContentLocale[];
    isLocalesLoading: boolean;
    onLocaleChange: (locale: string) => void;
    selectedLocale: string;
    selectedLocaleMeta: ConsentContentLocale | null;
};

export const ConsentGovernanceHeader = ({
    availableContentLocales,
    isLocalesLoading,
    onLocaleChange,
    selectedLocale,
    selectedLocaleMeta,
}: ConsentGovernanceHeaderProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const fieldBackgroundSx = {
        "& .MuiOutlinedInput-root": {
            bgcolor: theme.palette.common.white,
            borderRadius: theme.spacing(1.5),
        },
    };

    return (
        <Stack gap={theme.spacing(1.5)}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={theme.spacing(2)}>
                <Stack gap={theme.spacing(1)}>
                    <Typography variant="h2" sx={{ fontSize: "24px", fontWeight: 700 }}>
                        {t("settings.consent.title", "Consent governance")}
                    </Typography>
                    <Typography variant="body" sx={{ color: theme.custom.grey[2000], maxWidth: "860px" }}>
                        {t(
                            "settings.consent.description",
                            "Manage the consent catalog, published versions, and recorded user decisions from the administration area.",
                        )}
                    </Typography>
                </Stack>
                <TextField
                    select
                    size="small"
                    label={t("settings.consent.locale.label", "Content language")}
                    value={selectedLocale}
                    onChange={(event) => onLocaleChange(event.target.value)}
                    disabled={isLocalesLoading || availableContentLocales.length === 0}
                    sx={{ minWidth: 260, ...fieldBackgroundSx }}
                    helperText={
                        selectedLocaleMeta ?
                            t("settings.consent.locale.helper_selected", {
                                defaultValue:
                                    "Editing content in {{localeName}}. Missing translations use the default language until completed.",
                                localeName:
                                    selectedLocaleMeta.nativeName || selectedLocaleMeta.name || selectedLocaleMeta.code,
                            })
                        :   t(
                                "settings.consent.locale.helper_default",
                                "Editing content in the selected language. Missing translations use the default language until completed.",
                            )
                    }
                >
                    {availableContentLocales.map((locale) => (
                        <MenuItem key={locale.code} value={locale.code}>
                            {`${locale.nativeName || locale.name || locale.code} (${locale.code})`}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>
        </Stack>
    );
};
